import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { BehaviorSubject } from "rxjs";
import { LOCAL_STORAGE_KEYS } from "../global/constant";
import { NzNotificationService } from "ng-zorro-antd/notification";

@Injectable({
  providedIn: "root",
})
export class SharedService {
  constructor(
    private router: Router,
    private notification: NzNotificationService,
  ) {
    const storedCompanyId = this.getStoredCompanyId();
    if (storedCompanyId !== null) {
      this.companyId.next(storedCompanyId);
    }
  }

  getSidebarMenus(): any[] | null {
    const raw =
      localStorage.getItem(LOCAL_STORAGE_KEYS.sidebar_menu) ??
      sessionStorage.getItem(LOCAL_STORAGE_KEYS.sidebar_menu);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  setSidebarMenus(menus: any[]): void {
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.sidebar_menu,
      JSON.stringify(menus),
    );
    sessionStorage.removeItem(LOCAL_STORAGE_KEYS.sidebar_menu);
  }

  getStoredCompanyId(): number | null {
    const raw =
      localStorage.getItem(LOCAL_STORAGE_KEYS.selectedCompanyId) ??
      sessionStorage.getItem(LOCAL_STORAGE_KEYS.selectedCompanyId);
    if (raw === null || raw === "") {
      return null;
    }
    const parsedId = parseInt(raw, 10);
    return isNaN(parsedId) ? null : parsedId;
  }

  setStoredCompanyId(id: number): void {
    localStorage.setItem(LOCAL_STORAGE_KEYS.selectedCompanyId, String(id));
    sessionStorage.removeItem(LOCAL_STORAGE_KEYS.selectedCompanyId);
  }

  isSpinnerShow = new BehaviorSubject<boolean>(false);

  private subMenuList = new BehaviorSubject<any>([]);
  $subMenuList = this.subMenuList.asObservable();

  private companyId = new BehaviorSubject<number>(-1);
  $companyId = this.companyId.asObservable();

  showSpinner() {
    this.isSpinnerShow.next(true);
  }

  hideSpinner() {
    this.isSpinnerShow.next(false);
  }

  gotopage(url: any, newTab: boolean = false) {
    if (newTab) {
      const fullUrl = location.origin + url;
      window.open(fullUrl, "_blank");
    } else {
      this.router.navigateByUrl(url);
    }
  }

  successToastNotification(message: string) {
    if (message) {
      this.notification.success("Success", message, {
        nzClass: "success-notification-class",
      });
    }
  }

  errorToastNotification(message: string) {
    if (message) {
      this.notification.error("Error", message, {
        nzClass: "error-notification-class",
        nzDuration: 2000,
      });
    }
  }

  //#region Create Object
  createObjectFromMultipleKeys(values: { [key: string]: any }): {
    [key: string]: any;
  } {
    const obj: { [key: string]: any } = {};

    for (const key in values) {
      if (Object.prototype.hasOwnProperty.call(values, key)) {
        obj[key] = values[key];
      }
    }

    return obj;
  }
  //#endregion

  setSubMenuList(data: any) {
    this.subMenuList.next(data);
  }

  setCompanyId(id: number) {
    const numericId =
      typeof (id as any) === "string" ? parseInt(id as any, 10) : id;
    if (!isNaN(numericId as any)) {
      this.companyId.next(numericId as number);
      this.setStoredCompanyId(numericId as number);
    }
  }

  getCompanyId() {
    return this.companyId.getValue();
  }

  //#region Logout
  logout(): void {
    localStorage.removeItem(LOCAL_STORAGE_KEYS.accessToken);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.loginUserDetails);
    //localStorage.removeItem(LOCAL_STORAGE_KEYS.sidebar_menu);
    localStorage.clear();
    sessionStorage.clear();
    this.router.navigateByUrl("/auth/login");
  }
  //#endregion

  //#region Message Toast Notification
  messageToastNotification(type: string, message: string): void {
    if (message) {
      this.notification.create(
        type,
        type.charAt(0).toUpperCase() + type.slice(1),
        message,
        {
          nzClass: `${type}-notification-class`,
          nzDuration: type === "error" ? 2000 : 3000,
        },
      );
    }
  }
  //#endregion

  // Helper to return keys safely (no error if obj is undefined)
  getKeys(obj: any): string[] {
    return obj ? Object.keys(obj) : [];
  }

  /**
   * Return a string that describes how long ago the given date was, e.g. "Updated 2 hours ago".
   * If the given date is in the future, return an empty string.
   * If the given date is undefined, return an empty string.
   * @param {Date|string} date The date to calculate the time ago string for
   * @returns {string} A string describing how long ago the given date was
   */
  /*******  4a041981-e6bc-4ab0-9de2-84d6dbc76ef9  *******/
  timeAgo(date: Date | string, returnUpdateStr: boolean = true): string {
    if (date) {
      const d = new Date(date);
      const now = new Date();

      const seconds = Math.floor((now.getTime() - d.getTime()) / 1000);

      if (seconds < 60)
        return `${returnUpdateStr ? "Updated " : ""} ${seconds} seconds ago`;

      const minutes = Math.floor(seconds / 60);
      if (minutes < 60)
        return `${returnUpdateStr ? "Updated " : ""} ${minutes} minutes ago`;

      const hours = Math.floor(minutes / 60);
      if (hours < 24)
        return `${returnUpdateStr ? "Updated " : ""} ${hours} hours ago`;

      const days = Math.floor(hours / 24);
      if (days < 30)
        return `${returnUpdateStr ? "Updated " : ""} ${days} days ago`;

      const months = Math.floor(days / 30);
      if (months < 12)
        return `${returnUpdateStr ? "Updated " : ""} ${months} months ago`;

      const years = Math.floor(months / 12);
      return `${returnUpdateStr ? "Updated " : ""} ${years} years ago`;
    } else {
      return "";
    }
  }

  /**
   * Format a date range as a string.
   * If the start and end dates are in the same year, the year is only shown once.
   * If the start and end dates are in different years, the year is shown for each date.
   * Example 1: Jan 15 – Mar 31, 2025
   * Example 2: Dec 15, 2024 – Jan 10, 2025
   * @param {Date|string} start The start date of the range
   * @param {Date|string} end The end date of the range
   * @returns {string} A string representing the date range
   */
  formatDateRange(start: Date | string, end: Date | string): string {
    if (start && end) {
      const s = new Date(start);
      const e = new Date(end);

      const sameYear = s.getFullYear() === e.getFullYear();

      const monthShort = (d: Date) =>
        d.toLocaleDateString("en-US", { month: "short" });

      if (sameYear) {
        // Example: Jan 15 – Mar 31, 2025
        return `${monthShort(s)} ${s.getDate()} – ${monthShort(e)} ${e.getDate()}, ${s.getFullYear()}`;
      } else {
        // Example: Dec 15, 2024 – Jan 10, 2025
        return `${monthShort(s)} ${s.getDate()}, ${s.getFullYear()} – ${monthShort(e)} ${e.getDate()}, ${e.getFullYear()}`;
      }
    } else {
      return "";
    }
  }

  getInitials(name: string): string {
    if (!name) return "";

    const words = name.trim().split(/\s+/);

    if (words.length === 1) {
      return words[0][0].toUpperCase();
    }

    return (words[0][0] + words[1][0]).toUpperCase();
  }

  getDocumentType(fileName: string): string {
    if (!fileName) return "";
    const lastDotIndex = fileName.lastIndexOf(".");
    return lastDotIndex !== -1
      ? fileName.substring(lastDotIndex + 1).toLowerCase()
      : "unknown";
  }

  formatjson(jsonString: string): any {
    try {
      const parsedJson = JSON.parse(jsonString);
      return parsedJson;
    } catch (error) {
       return null;
    }
  }

  public getFirstCharacter(name: string, isfull: boolean = false): string {
    if (!name) return "";
    const parts = name.trim().split(/\s+/);
    const firstInitial = parts[0]?.charAt(0).toUpperCase() || "";
    if (isfull || parts.length === 1) {
      return firstInitial;
    }
    const lastInitial = parts[parts.length - 1]?.charAt(0).toUpperCase() || "";
    return firstInitial + lastInitial;
  }
}
